import React, { useCallback } from 'react';
import { t } from '@lingui/core/macro';

import { Box, Button, Heading } from '@rover/kibble/core';
import BottomSheet from '@rover/kibble/official-patterns/BottomSheet';
import type { ApiFrontendConfigurationRetrieve200SettingsDogSizeBuckets } from '@rover/rsdk/src/apiClient/latest/model/apiFrontendConfigurationRetrieve200SettingsDogSizeBuckets';
import { emitAnalyticsEvent } from '@rover/rsdk/src/modules/Analytics';
import getIsCatOnlyPetType from '@rover/shared/js/search/utilities/getIsCatOnlyPetType';
import type { SearchFiltersOptions } from '@rover/types/src/apiResponses/SearchOptionsResponse';
import type { Pet } from '@rover/types/src/Pet';
import type { SearchFilters } from '@rover/types/src/SearchFilters';
import type { ServiceType } from '@rover/types/src/ServiceType';
import emitMetric from '@rover/utilities/emitMetric';

import { SearchFiltersClose } from '../../Filters/Collections/Wizard/analyticsEvents';
import { formatSelectedDays } from '../utils';

import PetSection from './PetSection';
import ServiceSection from './ServiceSection';

export type Props = {
  isOpen: boolean;
  onClose: (saved: boolean) => void;
  searchFilters: SearchFilters;
  isUserAuthenticated: boolean;
  pets: Pet[];
  updateFiltersAndFireSearch: (filters: Partial<SearchFilters>) => void;
  allServiceTypeFilterOptions: Record<string, SearchFiltersOptions>;
  language: string;
  petSizeBuckets: ApiFrontendConfigurationRetrieve200SettingsDogSizeBuckets;
  weightUnit: string;
  getServiceTypeBySlug: (arg0: string) => ServiceType | null | undefined;
};

const PrimaryFiltersBottomSheet = ({
  isOpen,
  onClose,
  searchFilters,
  updateFiltersAndFireSearch,
  isUserAuthenticated,
  pets,
  allServiceTypeFilterOptions,
  language,
  petSizeBuckets,
  weightUnit,
  getServiceTypeBySlug,
}: Props): JSX.Element => {
  const hasPets = isUserAuthenticated && pets.length > 0;
  const isCatOnly = getIsCatOnlyPetType(searchFilters.petType || []);

  const serviceTypeFilterOptions = allServiceTypeFilterOptions[searchFilters.serviceType] || {};

  const handleFiltersChange = useCallback(
    (filters: Partial<SearchFilters>) => {
      const daysBooleanMap = formatSelectedDays(filters.selectedDays || []);
      updateFiltersAndFireSearch({ ...searchFilters, ...filters, ...daysBooleanMap });
    },
    [searchFilters, updateFiltersAndFireSearch]
  );

  const handleCloseWithoutSaving = (): void => {
    onClose(false);
    emitMetric('filters-bottom-sheet-close', { sheet: 'primary-filters' });
    emitAnalyticsEvent(new SearchFiltersClose({ sheet: 'primary-filters' }));
  };

  return (
    <BottomSheet
      onRequestClose={handleCloseWithoutSaving}
      isOpen={isOpen}
      hideCloseIcon
      closeOnDragDown
      headerId="filters-title"
      contentPaddingStyles={{ padding: '0x' }}
    >
      <Heading id="filters-title" webProps={{ display: 'none' }}>{t`Filters`}</Heading>
      <Box px="4x" pb="20x" maxHeight="75vh" sx={{ overflowY: 'auto' }}>
        <ServiceSection
          serviceType={searchFilters.serviceType}
          onFiltersChange={handleFiltersChange}
          location={searchFilters.location}
          searchFilters={searchFilters}
          serviceTypeFilterOptions={serviceTypeFilterOptions}
          language={language}
          isCatOnly={isCatOnly}
        />
        <PetSection
          pets={hasPets ? pets : []}
          searchFilters={searchFilters}
          dogCount={searchFilters.dogCount || 0}
          catCount={searchFilters.catCount || 0}
          puppyCount={searchFilters.puppyCount || 0}
          onChange={handleFiltersChange}
          serviceTypeFilterOptions={serviceTypeFilterOptions}
          petSizeBuckets={petSizeBuckets}
          weightUnit={weightUnit}
          getServiceTypeBySlug={getServiceTypeBySlug}
          hasPets={hasPets}
          isCatOnly={isCatOnly}
        />
      </Box>
      <Box
        position="absolute"
        bottom="0"
        right="0"
        left="0"
        backgroundColor="white"
        p="4x"
        pb="6x"
        borderTop="1px solid"
        borderTopColor="separator.primary"
        zIndex="999"
      >
        <Button variant="primary" fullWidth onPress={() => onClose(true)}>
          {t`Done`}
        </Button>
      </Box>
    </BottomSheet>
  );
};

export default PrimaryFiltersBottomSheet;
